<script lang="ts">
  import { post } from "@util/api"
  import { ValidationErrors } from "@util/validations"

  import { Cancellation, canBeRefundedUsingStripe, validateCancellation } from "@models/cancellations/Cancellation"
  import { CostItem } from "@models/costs/CostItem"
  import { InsurancePlan } from "@models/insurance/InsurancePlan"
  import { Person } from "@models/profiles/Person"
  import { TotalByPaymentMethod } from "@models/payments/TotalByPaymentMethod"

  import Fieldset from "@components/ui/Fieldset.svelte"
  import Form from "@components/ui/Form.svelte"

  import PaymentInfo from "./CancellationForm/PaymentInfo.svelte"
  import NonRefundablesSection from "./CancellationForm/NonRefundablesSection.svelte"
  import RefundsSection from "./CancellationForm/RefundsSection.svelte"
  import CancellationSubmit from "./CancellationForm/CancellationSubmit.svelte"
  import CoverageDetails from "./CancellationForm/CoverageDetails.svelte"
  import PricingInfo from "./CancellationForm/PricingInfo.svelte"
  import AmountsOverview from "./CancellationForm/AmountsOverview.svelte"
  import { maybeExcludingInsurance, totalClaimable } from "@models/cancellations/NonRefundableAdjustment"
  import TotalsByPaymentMethod from "./CancellationForm/TotalsByPaymentMethod.svelte"

  export let path: string
  export let person: Person
  export let cancellation: Cancellation
  export let insurancePlan: InsurancePlan | undefined
  export let costItems: CostItem[]
  export let totalsByPaymentMethod: TotalByPaymentMethod[]
  export let participationId: number
  export let hasPendingPayment: boolean
  export let insuranceOnly: boolean

  function submit() {
    return post(path, {
      participationId: participationId,
      cancellation: {
        ...cancellation,
        nonRefundableAdjustments: maybeExcludingInsurance(cancellation.nonRefundableAdjustments, insuranceOnly),
      },
    })
  }

  function validate(): ValidationErrors<Cancellation> {
    return validateCancellation(cancellation)
  }

  function maybeDisplayPaymentSources(totalsByPaymentMethod) {
    return totalsByPaymentMethod.reduce((previousValue, currentValue) => previousValue + currentValue.total, 0) > 0
  }

  $: {
    if (cancellation.refundMethod === "stripe" && !canBeRefundedUsingStripe(cancellation, insuranceOnly)) {
      cancellation.refundMethod = "manual"
    }
  }
</script>

<Form data={cancellation} {submit} {validate} let:submitting let:submitted let:errors>
  <Fieldset legend="Pricing">
    <div slot="description">
      Pricing for {person.firstName}. Other passengers on this account are not included here.
    </div>

    <PricingInfo {person} {costItems} />
  </Fieldset>

  <Fieldset legend="Payment Breakdown">
    <div slot="description">
      Payments, fees and discounts for {person.firstName}. Other passengers on this account are not included here.
    </div>

    <PaymentInfo {person} {cancellation} />
  </Fieldset>

  <Fieldset legend="Non-Refundable Amounts">
    <div slot="description">
      <p>Which of {person.firstName}'s items are non-refundable as described by your terms and conditions.</p>
      <p>Convenience fees can only be refunded as part of a full refund.</p>
    </div>

    <NonRefundablesSection {person} bind:cancellation {insurancePlan} {errors} {submitted} {insuranceOnly} />
  </Fieldset>

  <Fieldset legend="Travel Protection">
    <div slot="description">
      <p>Details on selected travel protection and eligibility.</p>
      <p>
        All benefits are subject to the purchase of the travel protection plan, the written claim documentation provided to
        Trip Mate and the plan provisions.
      </p>
    </div>

    <CoverageDetails {insurancePlan} nonRefundableAdjustments={cancellation.nonRefundableAdjustments} {insuranceOnly} />
  </Fieldset>

  <AmountsOverview {cancellation} {insuranceOnly} />

  {#if maybeDisplayPaymentSources(totalsByPaymentMethod)}
    <Fieldset legend="Payment Sources">
      <div slot="description">
        Sum of payments made for {person.firstName} grouped by source.
      </div>

      <TotalsByPaymentMethod {totalsByPaymentMethod} />
    </Fieldset>
  {/if}

  <Fieldset legend="Refunding">
    <div slot="description">
      How refunds for {person.firstName} will be processed. Please note that:
      <ul class="mt-1 italic text-sm2">
        <li>Card refunds are processed on the same business day.</li>
        <li>Bank refunds can take 2-10 business days to process.</li>
      </ul>
    </div>

    <RefundsSection bind:cancellation {submitted} {errors} {insuranceOnly} />
  </Fieldset>

  <Fieldset legend="Confirm & Cancel">
    <div slot="description">
      <p>Once you cancel this passenger we will:</p>
      <ul class="mt-1 italic text-sm2">
        <li>Process refunds.</li>
        {#if insurancePlan && totalClaimable(cancellation.nonRefundableAdjustments) > 0}
          <li>Email the account owner with instructions for filing a travel protection claim.</li>
        {/if}
      </ul>
      <p class="font-bold text-gray-700">Cancelling a paying passenger is irreversible.</p>
    </div>

    <CancellationSubmit bind:cancellation {errors} {hasPendingPayment} {person} {submitted} {submitting} />
  </Fieldset>
</Form>
